import { APP_NAME } from '../../../lib/app-name';
import type { PlantDto, PlantEquipmentDto, PlantFlowKind, PlantSiteDto, PlantSupply } from '@bk/contracts';

/**
 * La pianta viva di una sede nel finale della configurazione (ADR 0135, che riprende l'ADR 0034): i reparti in
 * isometria intorno alla regia del prodotto, gli impianti animati per ciò che portano, i flussi colorati dagli impianti ai
 * reparti che servono e i dati che passano dalla regia verso i soggetti esterni. Disegno in SVG con createElementNS e
 * textContent: nessun testo dell'azienda finisce in markup.
 */

type Pt = [number, number];

/** Cosa porta un impianto: colore e animazione. Un vocabolario visivo chiuso, uguale per tutte le aziende. */
export const SUPPLY: Record<PlantSupply, { color: string; label: string }> = {
  energia: { color: '#ffe066', label: 'Energia' },
  calore: { color: '#ffb547', label: 'Calore' },
  freddo: { color: '#7ef0c8', label: 'Freddo' },
  aria: { color: '#a9dcff', label: 'Aria' },
  acqua: { color: '#36e2ff', label: 'Acqua' },
  dati: { color: '#b79cff', label: 'Dati' },
  sicurezza: { color: '#ff7a8a', label: 'Sicurezza' },
  movimento: { color: '#9fe870', label: 'Movimento' },
  lavorazione: { color: '#ff9f6b', label: 'Lavorazione' },
};
const OTHER = { color: '#cfc8bb', label: 'Altro' };
export const supplyOf = (e: PlantEquipmentDto) => (e.porta ? SUPPLY[e.porta] : OTHER);

/** I dati che passano dalla regia verso l'esterno, per tipo. */
export const DATA: Record<PlantFlowKind, { color: string; label: string }> = {
  clienti: { color: '#ffd59e', label: 'Clienti e richieste' },
  documenti: { color: '#c7b5ff', label: 'Contratti e documenti' },
  denaro: { color: '#fff0a8', label: 'Fatture e pagamenti' },
  regole: { color: '#9ff0d0', label: 'Regole e controlli' },
};

const ROOM_COLORS = ['#e8c9a0', '#8fb3e8', '#e89f7a', '#7fd8e6', '#c58fd0', '#b6d98a', '#ff9fc4', '#c9b48a'];
const SITE_ROOM = '#9aa3ad';

/* ---------------- Posti: la regia al centro di una griglia 3×3, i reparti intorno ---------------- */

const W = 7;
const D = 5;
const GAP = 1.1;
const SLOTS: Pt[] = [
  [1, 0],
  [0, 1],
  [2, 1],
  [1, 2],
  [0, 0],
  [2, 0],
  [0, 2],
  [2, 2],
];
const cell = ([c, r]: Pt): Pt => [c * (W + GAP), r * (D + GAP)];
const SPAN_X = 3 * W + 2 * GAP;
const SPAN_Y = 3 * D + 2 * GAP;

export interface LayoutEquipment {
  e: PlantEquipmentDto;
  x: number;
  y: number;
}
export interface LayoutRoom {
  /** `r:<reparto>`, `sede` per gli impianti senza reparto, `regia`. */
  id: string;
  nome: string;
  color: string;
  x: number;
  y: number;
  w: number;
  h: number;
  core?: boolean;
  eq: LayoutEquipment[];
  /** Programmi del reparto, per la riga sotto il nome. */
  programmi: string[];
}
export interface LayoutExternal {
  /** `e:<n>` o `pro` per i professionisti esterni insieme. */
  id: string;
  nome: string;
  x: number;
  y: number;
  pro?: boolean;
}
export interface SiteLayout {
  rooms: LayoutRoom[];
  ext: LayoutExternal[];
  /** Dagli impianti ai reparti che servono (e alla regia). */
  supply: Array<{ eq: string; from: Pt; to: string; kind: PlantSupply | null }>;
  /** Dai reparti ai soggetti esterni, passando dalla regia. */
  data: Array<{ room: string; ext: string; kind: PlantFlowKind; cosa: string; out: boolean }>;
}

const MAX_EQ = 8;
/** Sulla pianta i nomi lunghi si accorciano; nel pannello restano interi. */
export const short = (s: string, n = 26) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

/** Dove si disegna ogni cosa di una sede: tutto dai dati della pianta, niente di inventato. */
export function siteLayout(site: PlantSiteDto, plant: PlantDto): SiteLayout {
  const rooms: LayoutRoom[] = [];
  const loose = site.impianti.filter((e) => !e.reparto);
  const places = [
    ...site.reparti.slice(0, 7).map((r, i) => ({ id: `r:${r.id}`, nome: r.nome, color: ROOM_COLORS[i % ROOM_COLORS.length]!, eq: site.impianti.filter((e) => e.reparto === r.id), programmi: r.programmi })),
    ...(loose.length ? [{ id: 'sede', nome: 'Impianti della sede', color: SITE_ROOM, eq: loose, programmi: [] as string[] }] : []),
  ];
  places.slice(0, SLOTS.length).forEach((p, i) => {
    const [x, y] = cell(SLOTS[i]!);
    rooms.push({
      id: p.id,
      nome: p.nome,
      color: p.color,
      x,
      y,
      w: W,
      h: D,
      programmi: p.programmi,
      eq: p.eq.slice(0, MAX_EQ).map((e, k) => ({ e, x: x + 0.95 + (k % 4) * 1.6, y: y + (p.eq.length > 4 ? 1.5 : 2.4) + Math.floor(k / 4) * 1.9 })),
    });
  });
  const [cx, cy] = cell([1, 1]);
  rooms.push({ id: 'regia', nome: `Regia ${APP_NAME}`, color: '#e8c9a0', x: cx + 0.6, y: cy + 0.5, w: W - 1.2, h: D - 1, core: true, eq: [], programmi: [] });

  // Gli esterni con cui la sede scambia; i consulenti stanno tutti nei «Professionisti esterni».
  const ends = new Set(site.flussi.flatMap((f) => [f.da, f.a]).filter((k) => k.startsWith('e:')));
  const pros = plant.esterni.some((e) => e.categoria === 'consulenti');
  const others = [...ends].filter((k) => plant.esterni[Number(k.slice(2))]?.categoria !== 'consulenti').slice(0, 8);
  const sides: Array<(t: number) => Pt> = [
    (t) => [t * SPAN_X, -3],
    (t) => [-3.2, t * SPAN_Y],
    (t) => [SPAN_X + 3.2, t * SPAN_Y],
    (t) => [t * SPAN_X, SPAN_Y + 3],
  ];
  const spots: Pt[] = [sides[0]!(0.18), sides[1]!(0.3), sides[2]!(0.3), sides[0]!(0.55), sides[1]!(0.72), sides[2]!(0.72), sides[0]!(0.9), sides[3]!(pros ? 0.15 : 0.5)];
  const ext: LayoutExternal[] = others.map((id, i) => ({ id, nome: plant.esterni[Number(id.slice(2))]!.nome, x: spots[i]![0], y: spots[i]![1] }));
  if (pros) {
    const [x, y] = sides[3]!(0.6);
    ext.push({ id: 'pro', nome: 'Professionisti esterni', x, y, pro: true });
  }

  const roomIds = new Set(rooms.map((r) => r.id));
  const supply: SiteLayout['supply'] = [];
  for (const room of rooms)
    for (const q of room.eq) {
      const targets = new Set([...q.e.serve.map((s) => (s === 'regia' ? 'regia' : `r:${s}`)), ...(q.e.rete ? site.reparti.map((r) => `r:${r.id}`) : [])]);
      targets.delete(room.id);
      for (const to of targets) if (roomIds.has(to)) supply.push({ eq: q.e.id, from: [q.x + 0.4, q.y + 0.4], to, kind: q.e.porta });
    }
  const extOf = (k: string) => (plant.esterni[Number(k.slice(2))]?.categoria === 'consulenti' ? (pros ? 'pro' : null) : ext.some((x) => x.id === k) ? k : null);
  const roomOf = (k: string) => (k === 'impianti' ? (roomIds.has('sede') ? 'sede' : null) : roomIds.has(k) ? k : null);
  const data: SiteLayout['data'] = [];
  const seen = new Set<string>();
  for (const f of site.flussi) {
    const out = f.a.startsWith('e:');
    const room = roomOf(out ? f.da : f.a);
    const e = extOf(out ? f.a : f.da);
    if (!room || !e) continue;
    const key = `${room}|${e}|${f.tipo}|${out}`;
    if (seen.has(key)) continue;
    seen.add(key);
    data.push({ room, ext: e, kind: f.tipo, cosa: f.cosa, out });
  }
  return { rooms, ext, supply, data };
}

/* ---------------- Disegno ---------------- */

const S = 30;
const P = (x: number, y: number, z = 0): Pt => [(x - y) * 0.866 * S, (x + y) * 0.5 * S - z * S];
const pts = (a: Pt[]) => a.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
const NS = 'http://www.w3.org/2000/svg';
function el(tag: string, attrs: Record<string, string | number>, parent?: Element): SVGElement {
  const n = document.createElementNS(NS, tag) as SVGElement;
  for (const k in attrs) n.setAttribute(k, String(attrs[k]));
  parent?.appendChild(n);
  return n;
}
const shade = (hex: string, f: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgb(${[n >> 16, (n >> 8) & 255, n & 255].map((c) => Math.round(Math.min(255, c * f))).join(',')})`;
};

/** L'animazione di un impianto secondo ciò che porta. */
function glyph(g: Element, kind: PlantSupply | null, x: number, y: number, color: string) {
  const a = el('g', { class: 'anim' }, g);
  const line = (x1: number, y1: number, x2: number, y2: number, w: number, p: Element) => el('line', { x1, y1, x2, y2, stroke: color, 'stroke-width': w, 'stroke-linecap': 'round' }, p);
  switch (kind) {
    case 'aria': {
      const f = el('g', { class: 'fx-spin' }, a);
      el('circle', { cx: x, cy: y, r: 7, fill: 'none', stroke: color, 'stroke-width': 1.2, opacity: 0.6 }, f);
      for (let i = 0; i < 3; i++) line(x, y, x + Math.cos(i * 2.094) * 6, y + Math.sin(i * 2.094) * 6, 2, f);
      break;
    }
    case 'calore':
      el('path', { d: `M${x} ${y - 9} C ${x + 6} ${y - 3}, ${x + 5} ${y + 5}, ${x} ${y + 5} C ${x - 5} ${y + 5}, ${x - 6} ${y - 2}, ${x} ${y - 9} Z`, fill: color, class: 'fx-pulse' }, a);
      break;
    case 'acqua': {
      const w = el('g', { class: 'fx-bob' }, a);
      el('path', { d: `M${x - 9} ${y} q 3 -4 6 0 t 6 0 t 6 0`, fill: 'none', stroke: color, 'stroke-width': 2, 'stroke-linecap': 'round' }, w);
      el('path', { d: `M${x - 9} ${y + 5} q 3 -4 6 0 t 6 0 t 6 0`, fill: 'none', stroke: color, 'stroke-width': 1.4, opacity: 0.6, 'stroke-linecap': 'round' }, w);
      break;
    }
    case 'energia':
      el('path', { d: `M${x + 2} ${y - 9} L ${x - 5} ${y + 1} L ${x} ${y + 1} L ${x - 2} ${y + 9} L ${x + 5} ${y - 2} L ${x} ${y - 2} Z`, fill: color, class: 'fx-pulse' }, a);
      break;
    case 'freddo': {
      const f = el('g', { class: 'fx-spin fx-slow' }, a);
      for (let i = 0; i < 3; i++) line(x - Math.cos(i * 1.047) * 7, y - Math.sin(i * 1.047) * 7, x + Math.cos(i * 1.047) * 7, y + Math.sin(i * 1.047) * 7, 1.6, f);
      break;
    }
    case 'dati':
      [3, 6, 9].forEach((r, i) => el('path', { d: `M${x - r} ${y + 2} A ${r} ${r} 0 0 1 ${x + r} ${y + 2}`, fill: 'none', stroke: color, 'stroke-width': 1.6, class: 'fx-pulse', style: `animation-delay:${i * 0.3}s` }, a));
      break;
    case 'sicurezza':
      el('circle', { cx: x, cy: y, r: 4, fill: color, class: 'fx-pulse' }, a);
      el('circle', { cx: x, cy: y, r: 9, fill: 'none', stroke: color, 'stroke-width': 1.2, class: 'fx-pulse', style: 'animation-delay:.6s' }, a);
      break;
    case 'movimento': {
      const m = el('g', { class: 'fx-lift' }, a);
      el('path', { d: `M${x - 5} ${y - 2} L ${x} ${y - 7} L ${x + 5} ${y - 2}`, fill: 'none', stroke: color, 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, m);
      el('path', { d: `M${x - 5} ${y + 4} L ${x} ${y - 1} L ${x + 5} ${y + 4}`, fill: 'none', stroke: color, 'stroke-width': 1.4, opacity: 0.6, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }, m);
      break;
    }
    case 'lavorazione': {
      const f = el('g', { class: 'fx-spin' }, a);
      el('circle', { cx: x, cy: y, r: 4.5, fill: 'none', stroke: color, 'stroke-width': 2 }, f);
      for (let i = 0; i < 6; i++) line(x + Math.cos(i * 1.047) * 5, y + Math.sin(i * 1.047) * 5, x + Math.cos(i * 1.047) * 8, y + Math.sin(i * 1.047) * 8, 2.2, f);
      break;
    }
    default:
      el('circle', { cx: x, cy: y, r: 4, fill: color, class: 'fx-pulse' }, a);
  }
}

interface Flow {
  path: SVGPathElement;
  len: number;
  dots: SVGElement[];
  owner: string | null;
  room: string;
  off: number;
  speed: number;
}

export interface SceneHandlers {
  onSelect: (id: string) => void;
  onToggle: (equipmentId: string) => void;
}

/** La scena di una sede: si disegna una volta, poi si aggiornano stati e selezione senza ridisegnare. */
export class FinaleScene {
  private flows: Flow[] = [];
  private raf = 0;
  private off = new Set<string>();
  private readonly reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(
    private readonly svg: SVGSVGElement,
    private readonly lay: SiteLayout,
    private readonly glow: string,
    private readonly on: SceneHandlers,
  ) {
    this.draw();
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    this.svg.textContent = '';
  }

  setOff(off: Set<string>) {
    this.off = off;
    this.svg.querySelectorAll<SVGGElement>('.fx-imp').forEach((g) => {
      const isOff = off.has(g.dataset.id!);
      g.classList.toggle('off', isOff);
      g.querySelector('.led')?.setAttribute('fill', isOff ? '#ff6b6b' : '#3cff9b');
      g.setAttribute('aria-pressed', String(!isOff));
    });
    if (this.reduce) this.frame(0);
  }

  select(id: string | null) {
    this.svg.querySelectorAll<SVGGElement>('[data-room]').forEach((g) => g.classList.toggle('sel', g.dataset.room === id));
    this.svg.querySelectorAll<SVGGElement>('.fx-ext.pro').forEach((g) => g.classList.toggle('sel', id === 'pro'));
  }

  private draw() {
    const { svg, lay, glow } = this;
    svg.textContent = '';
    const all: Pt[] = [];
    lay.rooms.forEach((r) => all.push(P(r.x, r.y), P(r.x + r.w, r.y), P(r.x, r.y + r.h), P(r.x + r.w, r.y + r.h), P(r.x, r.y, 2.4)));
    lay.ext.forEach((e) => all.push(P(e.x, e.y)));
    const minX = Math.min(...all.map((p) => p[0])) - 100;
    const maxX = Math.max(...all.map((p) => p[0])) + 100;
    const minY = Math.min(...all.map((p) => p[1])) - 50;
    const maxY = Math.max(...all.map((p) => p[1])) + 40;
    svg.setAttribute('viewBox', `${minX.toFixed(0)} ${minY.toFixed(0)} ${(maxX - minX).toFixed(0)} ${(maxY - minY).toFixed(0)}`);
    const defs = el('defs', {}, svg);
    const ag = el('radialGradient', { id: 'fx-aura' }, defs);
    el('stop', { offset: '0%', 'stop-color': glow, 'stop-opacity': 0.3 }, ag);
    el('stop', { offset: '55%', 'stop-color': glow, 'stop-opacity': 0.1 }, ag);
    el('stop', { offset: '100%', 'stop-color': glow, 'stop-opacity': 0 }, ag);
    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;
    el('ellipse', { cx: midX, cy: midY + 10, rx: (maxX - minX) * 0.55, ry: (maxY - minY) * 0.55, fill: 'url(#fx-aura)', class: 'fx-aura' }, svg);
    el('ellipse', { cx: midX, cy: midY + 10, rx: (maxX - minX) * 0.44, ry: (maxY - minY) * 0.4, fill: 'none', stroke: glow, 'stroke-opacity': 0.25, 'stroke-dasharray': '1 10', 'stroke-width': 1.5, class: 'fx-ring' }, svg);
    const grid = el('g', { opacity: 0.12 }, svg);
    for (let i = -2; i <= SPAN_X + 2; i += 1.5) {
      const [a, b] = [P(i, -2), P(i, SPAN_Y + 2)];
      el('line', { x1: a[0], y1: a[1], x2: b[0], y2: b[1], stroke: '#fff', 'stroke-width': 0.5 }, grid);
    }
    for (let j = -2; j <= SPAN_Y + 2; j += 1.5) {
      const [a, b] = [P(-2, j), P(SPAN_X + 2, j)];
      el('line', { x1: a[0], y1: a[1], x2: b[0], y2: b[1], stroke: '#fff', 'stroke-width': 0.5 }, grid);
    }
    const roomsG = el('g', {}, svg);
    const flowG = el('g', {}, svg);
    const eqG = el('g', {}, svg);
    const partG = el('g', {}, svg);
    const lblG = el('g', {}, svg);
    const extG = el('g', {}, svg);
    const key = (g: Element, fn: () => void) => {
      g.addEventListener('click', fn);
      g.addEventListener('keydown', (e) => {
        const k = (e as KeyboardEvent).key;
        if (k === 'Enter' || k === ' ') {
          e.preventDefault();
          fn();
        }
      });
    };

    [...lay.rooms].sort((a, b) => a.x + a.y - (b.x + b.y)).forEach((r) => {
      const g = el('g', { class: `fx-room${r.core ? ' core' : ''}`, 'data-room': r.id, tabindex: 0, role: 'button', 'aria-label': r.nome }, roomsG);
      const wall = r.core ? 0.25 : 0.55;
      el('polygon', { points: pts([P(r.x, r.y), P(r.x + r.w, r.y), P(r.x + r.w, r.y, wall), P(r.x, r.y, wall)]), fill: shade(r.color, 0.55), 'fill-opacity': 0.55 }, g);
      el('polygon', { points: pts([P(r.x, r.y), P(r.x, r.y + r.h), P(r.x, r.y + r.h, wall), P(r.x, r.y, wall)]), fill: shade(r.color, 0.4), 'fill-opacity': 0.55 }, g);
      el('polygon', { class: 'floor', points: pts([P(r.x, r.y), P(r.x + r.w, r.y), P(r.x + r.w, r.y + r.h), P(r.x, r.y + r.h)]), fill: r.color, 'fill-opacity': r.core ? 0.3 : 0.2, stroke: r.color, 'stroke-opacity': 0.85, 'stroke-width': 1.2 }, g);
      if (r.core) {
        const c = P(r.x + r.w / 2, r.y + r.h / 2);
        el('ellipse', { cx: c[0], cy: c[1], rx: r.w * S * 0.55, ry: r.h * S * 0.3, fill: 'none', stroke: glow, 'stroke-width': 1.5, class: 'fx-pulse' }, g);
        el('ellipse', { cx: c[0], cy: c[1], rx: r.w * S * 0.3, ry: r.h * S * 0.17, fill: glow, 'fill-opacity': 0.25, class: 'fx-aura' }, g);
      }
      key(g, () => this.on.onSelect(r.id));
      const lp = P(r.x + r.w / 2, r.y + 0.9, r.eq.length ? 2.3 : 1.2);
      const t = el('text', { x: lp[0], y: lp[1], 'text-anchor': 'middle', class: 'fx-lbl' }, lblG);
      t.textContent = short(r.nome);
      const sub = r.core ? 'tutti i dati passano di qui' : r.programmi.slice(0, 2).join(' · ') + (r.programmi.length > 2 ? ' · …' : '');
      if (sub) {
        const t2 = el('text', { x: lp[0], y: lp[1] + 15, 'text-anchor': 'middle', class: 'fx-lbl sm' }, lblG);
        t2.textContent = short(sub, 40);
      }
    });

    const eqs = lay.rooms.flatMap((r) => r.eq).sort((a, b) => a.x + a.y - (b.x + b.y));
    for (const q of eqs) {
      const x = q.x;
      const y = q.y;
      const s = 0.8;
      const z = 0.7;
      const col = supplyOf(q.e).color;
      const g = el('g', { class: `fx-imp${q.e.stato === 'presente' ? '' : ' maybe'}`, 'data-id': q.e.id, tabindex: 0, role: 'button', 'aria-pressed': 'true', 'aria-label': `${q.e.nome}: tocca per spegnere o accendere` }, eqG);
      el('polygon', { class: 'face', points: pts([P(x, y + s), P(x + s, y + s), P(x + s, y + s, z), P(x, y + s, z)]), fill: '#2b3140' }, g);
      el('polygon', { class: 'face', points: pts([P(x + s, y), P(x + s, y + s), P(x + s, y + s, z), P(x + s, y, z)]), fill: '#3a4256' }, g);
      el('polygon', { class: 'face', points: pts([P(x, y, z), P(x + s, y, z), P(x + s, y + s, z), P(x, y + s, z)]), fill: '#566079', stroke: col, 'stroke-opacity': 0.7 }, g);
      const led = P(x + s, y + s * 0.5, z * 0.6);
      el('circle', { class: 'led', cx: led[0], cy: led[1], r: 2.2, fill: '#3cff9b' }, g);
      const top = P(x + s / 2, y + s / 2, z + 0.75);
      glyph(g, q.e.porta, top[0], top[1], col);
      const title = el('title', {}, g);
      title.textContent = q.e.nome;
      key(g, () => this.on.onToggle(q.e.id));
    }

    for (const x of lay.ext) {
      const p = P(x.x, x.y);
      const label = short(x.nome, 28);
      const w = Math.max(80, label.length * 7 + 26);
      const g = el('g', { class: `fx-ext${x.pro ? ' pro' : ''}` }, extG);
      el('rect', { x: p[0] - w / 2, y: p[1] - 12, width: w, height: 24, rx: 12 }, g);
      const t = el('text', { x: p[0], y: p[1] + 4, 'text-anchor': 'middle' }, g);
      t.textContent = label;
      if (x.pro) {
        g.setAttribute('tabindex', '0');
        g.setAttribute('role', 'button');
        g.setAttribute('aria-label', 'Professionisti esterni: apri l’elenco');
        key(g, () => this.on.onSelect('pro'));
      } else {
        const title = el('title', {}, g);
        title.textContent = x.nome;
      }
    }

    // Flussi: dagli impianti ai reparti che servono; i dati verso l'esterno passano dalla regia.
    const regia = lay.rooms.find((r) => r.core)!;
    const center = (id: string, z = 0.2): Pt | null => {
      const r = lay.rooms.find((q) => q.id === id);
      if (r) return P(r.x + r.w / 2, r.y + r.h / 2, z);
      const e = lay.ext.find((q) => q.id === id);
      return e ? P(e.x, e.y) : null;
    };
    const via = center('regia')!;
    const curve = (a: Pt, b: Pt, lift: number) => `Q ${((a[0] + b[0]) / 2).toFixed(1)} ${(Math.min(a[1], b[1]) - lift).toFixed(1)} ${b[0].toFixed(1)} ${b[1].toFixed(1)}`;
    const add = (d: string, color: string, owner: string | null, room: string, packet: boolean) => {
      const path = el('path', { d, fill: 'none', stroke: color, 'stroke-opacity': packet ? 0.22 : 0.34, 'stroke-width': packet ? 1 : 1.3, 'stroke-dasharray': packet ? '2 6' : '3 5' }, flowG) as SVGPathElement;
      const len = path.getTotalLength();
      const dots = Array.from({ length: Math.max(2, Math.round(len / 95)) }, () =>
        packet ? el('rect', { width: 4, height: 4, rx: 1, fill: color, opacity: 0.95 }, partG) : el('circle', { r: 2.6, fill: color, opacity: 0.95 }, partG),
      );
      this.flows.push({ path, len, dots, owner, room, off: Math.random(), speed: packet ? 0.15 : 0.1 });
    };
    for (const f of lay.supply) {
      const a = P(f.from[0], f.from[1], 1.1);
      const b = center(f.to);
      if (b) add(`M${a[0].toFixed(1)} ${a[1].toFixed(1)} ${curve(a, b, 46)}`, f.kind ? SUPPLY[f.kind].color : OTHER.color, f.eq, f.to, false);
    }
    for (const f of lay.data) {
      const r = center(f.room, 0.4);
      const e = center(f.ext);
      if (!r || !e || f.room === regia.id) continue;
      const [a, b] = f.out ? [r, e] : [e, r];
      add(`M${a[0].toFixed(1)} ${a[1].toFixed(1)} ${curve(a, via, 40)} ${curve(via, b, 40)}`, DATA[f.kind].color, null, f.room, true);
    }
    this.frame(0);
    if (!this.reduce) {
      let last = performance.now();
      const tick = (now: number) => {
        this.frame(Math.min(0.05, (now - last) / 1000));
        last = now;
        this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }
  }

  /** Muove i pacchetti; un impianto spento ferma i suoi flussi. Con il movimento ridotto restano fermi. */
  private frame(dt: number) {
    for (const f of this.flows) {
      const on = !f.owner || !this.off.has(f.owner);
      f.path.style.display = on ? '' : 'none';
      f.off = (f.off + dt * f.speed) % 1;
      f.dots.forEach((dot, i) => {
        dot.style.display = on ? '' : 'none';
        if (!on) return;
        const p = f.path.getPointAtLength(((f.off + i / f.dots.length) % 1) * f.len);
        if (dot.tagName === 'rect') {
          dot.setAttribute('x', (p.x - 2).toFixed(1));
          dot.setAttribute('y', (p.y - 2).toFixed(1));
        } else {
          dot.setAttribute('cx', p.x.toFixed(1));
          dot.setAttribute('cy', p.y.toFixed(1));
        }
      });
    }
  }
}

/* ---------------- Colori: ogni sede prende un colore del marchio ---------------- */

const rgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as [number, number, number];
/** Mescola due colori: t = quanto del secondo. */
export function mix(a: string, b: string, t: number): string {
  const x = rgb(a);
  const y = rgb(b);
  return `#${x.map((c, i) => Math.round(c + (y[i]! - c) * t).toString(16).padStart(2, '0')).join('')}`;
}
/** Il colore di una sede, la sua notte e la sua aura. */
export function siteTheme(colori: string[], i: number): { base: string; night: string; glow: string } {
  const palette = colori.length ? colori : ['#5b7cfa', '#26cad3', '#c58fd0'];
  const base = palette[i % palette.length]!;
  return { base, night: mix(base, '#090b14', 0.86), glow: mix(base, '#ffffff', 0.55) };
}
